'use client';

import { Skeleton } from '@/components/common/feedback/skeleton/skeleton';
import { Button } from '@/components/common/inputs/button/button';
import { Card } from '@/components/common/layout/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

export type CaseStatus = 'Positive' | 'Neutral' | 'Negative';
export type FileType = 'pdf' | 'doc';

export interface Case {
  id: string;
  name: string;
  location: string;
  date: string;
  status: CaseStatus;
  fileType: FileType;
}

export interface CaseStats {
  totalCases: number;
  similarCases: number;
  veryGood: number;
  fair: number;
  poor: number;
}

export interface CaseOutcomeDetailProps {
  stats: CaseStats;
  cases: Case[];
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onDownload?: (caseId: string) => void;
  onFilterChange?: (filter: CaseStatus | 'All') => void;
  selectedFilter?: CaseStatus | 'All';
  isLoading?: boolean;
}

export const mockCaseStats: CaseStats = {
  totalCases: 150,
  similarCases: 75,
  veryGood: 60,
  fair: 45,
  poor: 45,
};

export const mockCases: Case[] = [
  {
    id: '1',
    name: 'Johnson v. State',
    location: 'California Supreme Court',
    date: '2023-05-15',
    status: 'Positive',
    fileType: 'pdf',
  },
  {
    id: '2',
    name: 'Smith v. Corporation',
    location: 'Federal District Court',
    date: '2023-04-22',
    status: 'Neutral',
    fileType: 'doc',
  },
  {
    id: '3',
    name: 'Williams v. Insurance Co.',
    location: 'State Appellate Court',
    date: '2023-03-10',
    status: 'Negative',
    fileType: 'pdf',
  },
  {
    id: '4',
    name: 'Davis v. Employer',
    location: 'Labor Tribunal',
    date: '2023-02-05',
    status: 'Positive',
    fileType: 'doc',
  },
  {
    id: '5',
    name: 'Brown v. Municipality',
    location: 'City Court',
    date: '2023-01-18',
    status: 'Neutral',
    fileType: 'pdf',
  },
  {
    id: '6',
    name: 'Garcia v. Healthcare Provider',
    location: 'Medical Arbitration Board',
    date: '2022-12-03',
    status: 'Negative',
    fileType: 'doc',
  },
  {
    id: '7',
    name: 'Lee v. Technology Company',
    location: 'Federal Circuit Court',
    date: '2022-11-15',
    status: 'Positive',
    fileType: 'pdf',
  },
  {
    id: '8',
    name: 'Martinez v. Real Estate Firm',
    location: 'State Superior Court',
    date: '2022-10-22',
    status: 'Neutral',
    fileType: 'doc',
  },
  {
    id: '9',
    name: 'Thompson v. Insurance Agency',
    location: 'District Court',
    date: '2022-09-07',
    status: 'Negative',
    fileType: 'pdf',
  },
  {
    id: '10',
    name: 'Anderson v. Educational Institution',
    location: 'Appellate Court',
    date: '2022-08-19',
    status: 'Positive',
    fileType: 'doc',
  },
  {
    id: '11',
    name: 'Rodriguez v. Bank',
    location: 'Federal Court',
    date: '2022-07-12',
    status: 'Positive',
    fileType: 'pdf',
  },
  {
    id: '12',
    name: 'Kim v. Tech Startup',
    location: 'Arbitration Panel',
    date: '2022-06-25',
    status: 'Neutral',
    fileType: 'doc',
  },
  {
    id: '13',
    name: 'Peterson v. Insurance Provider',
    location: 'State Court',
    date: '2022-05-30',
    status: 'Negative',
    fileType: 'pdf',
  },
];

export function CaseOutcomeDetail({
  stats = mockCaseStats,
  cases = mockCases,
  currentPage = 1,
  onPageChange = () => {},
  onDownload = () => {},
  onFilterChange = () => {},
  selectedFilter = 'All',
  isLoading = false,
}: CaseOutcomeDetailProps) {
  const [currentPageState, setCurrentPageState] = useState(currentPage);
  const [selectedFilterState, setSelectedFilterState] =
    useState(selectedFilter);

  const filters: (CaseStatus | 'All')[] = [
    'All',
    'Positive',
    'Neutral',
    'Negative',
  ];

  const ITEMS_PER_PAGE = 5;

  const filteredCases = useMemo(
    () =>
      selectedFilterState === 'All'
        ? cases
        : cases.filter((caseItem) => caseItem.status === selectedFilterState),
    [cases, selectedFilterState]
  );

  const totalPages = Math.ceil(filteredCases.length / ITEMS_PER_PAGE);

  const paginatedCases = useMemo(() => {
    const startIndex = (currentPageState - 1) * ITEMS_PER_PAGE;
    return filteredCases.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCases, currentPageState]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPageState(page);
      onPageChange(page);
    }
  };

  const handleFilterChange = (filter: CaseStatus | 'All') => {
    setSelectedFilterState(filter);
    setCurrentPageState(1);
    onFilterChange(filter);
  };

  if (isLoading) {
    return <CaseOutcomeDetailSkeleton />;
  }

  return (
    <div className='relative flex h-screen w-full flex-col bg-gray-50 dark:bg-gray-900'>
      <div className='flex h-full flex-col p-4 sm:p-6 md:p-8'>
        <div className='mx-auto flex h-full w-full flex-col space-y-4 sm:space-y-6'>
          {/* Stats Cards */}
          <div className='grid gap-4 sm:gap-6 md:grid-cols-2'>
            <Card className='rounded-lg bg-white shadow-lg dark:bg-gray-800'>
              <div className='rounded-t-lg border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-700/50 dark:bg-gray-800 sm:px-6'>
                <h2 className='text-base font-medium text-gray-900 dark:text-gray-100 sm:text-lg'>
                  Outcome Results
                </h2>
              </div>
              <div className='p-4 sm:p-6'>
                <div className='flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center'>
                  <div>
                    <div className='text-4xl font-bold text-gray-900 dark:text-gray-100 sm:text-5xl md:text-6xl'>
                      {stats.totalCases}
                    </div>
                    <div className='mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base'>
                      Similar Cases
                    </div>
                    <div className='mt-6 flex flex-wrap gap-3'>
                      <StatusPill
                        count={stats.veryGood}
                        label='Very Good'
                        color='text-emerald-600 dark:text-emerald-400'
                        bgColor='bg-emerald-100 dark:bg-emerald-900'
                      />
                      <StatusPill
                        count={stats.fair}
                        label='Fair'
                        color='text-amber-600 dark:text-amber-400'
                        bgColor='bg-amber-100 dark:bg-amber-900'
                      />
                      <StatusPill
                        count={stats.poor}
                        label='Poor'
                        color='text-rose-600 dark:text-rose-400'
                        bgColor='bg-rose-100 dark:bg-rose-900'
                      />
                    </div>
                  </div>
                  <CircularProgress
                    value={stats.similarCases}
                    total={stats.totalCases}
                    status={
                      stats.poor > Math.max(stats.veryGood, stats.fair)
                        ? 'Negative'
                        : stats.fair > Math.max(stats.poor, stats.veryGood)
                          ? 'Neutral'
                          : 'Positive'
                    }
                    label='Similar Cases'
                  />
                </div>
              </div>
              <div className='h-2 rounded-b-lg bg-gradient-to-r from-purple-500 to-purple-300 dark:from-purple-600 dark:to-purple-400'></div>
            </Card>

            <Card className='relative overflow-hidden rounded-lg bg-white shadow-lg dark:bg-gray-800'>
              <div className='rounded-t-lg border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-700/50 dark:bg-gray-800 sm:px-6'>
                <h2 className='text-base font-medium text-gray-900 dark:text-gray-100 sm:text-lg'>
                  Likelihood
                </h2>
              </div>
              <div className='p-4 sm:p-6'>
                <div className='flex flex-col'>
                  <div className='z-10 bg-black/10'>
                    <div className='text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl md:text-3xl'>
                      Positive Outcome
                    </div>
                    <p className='mt-2 max-w-[301px] text-sm text-gray-500 dark:text-gray-400 sm:text-base'>
                      Based on previous cases that were taken to court it seems
                      the outcome is mostly positive for rulings similar to your
                      request
                    </p>
                  </div>

                  {/* Centered shape at the bottom center of the card */}
                  <div className='z-[-1 absolute bottom-[-25%] left-1/2 -translate-x-1/2 transform'>
                    <div className='relative h-[207.52px] w-[172px] overflow-hidden'>
                      <div className='absolute left-[10px] top-[39px] h-[168.52px] w-[162px]'>
                        <div className='absolute left-0 top-0 h-[162px] w-[162px] rounded-full bg-gradient-to-b from-emerald-400 via-emerald-300 to-white blur-[15px] backdrop-blur-[100px] dark:from-emerald-500 dark:via-emerald-400 dark:to-gray-900'></div>
                        <div className='absolute left-0 top-[6.52px] h-[162px] w-[162px] rounded-full bg-white/0 blur-[30.40px] backdrop-blur-[51px]'></div>
                      </div>
                      <div className='absolute left-[56px] top-[74px] inline-flex h-[17px] items-center justify-center gap-1 rounded-full bg-emerald-100/30 px-2 py-1 dark:bg-emerald-900/30'>
                        <div className='h-2 w-2 rounded-full bg-gradient-to-bl from-emerald-400 to-emerald-200 shadow-[0px_4px_6.599999904632568px_0px_rgba(0,255,136,0.25)] dark:from-emerald-500 dark:to-emerald-300'></div>
                        <div className='text-center text-xs font-medium text-emerald-600 opacity-90 dark:text-emerald-400'>
                          Positive
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='h-2 rounded-b-lg bg-gradient-to-r from-purple-500 to-purple-300 dark:from-purple-600 dark:to-purple-400'></div>
            </Card>
          </div>

          {/* Cases List */}
          <Card className='flex flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-lg dark:bg-gray-800'>
            <div className='border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-700/50 dark:bg-gray-800 sm:px-6'>
              <div className='flex gap-4 overflow-x-auto sm:gap-8 md:gap-16'>
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => handleFilterChange(filter)}
                    className={`whitespace-nowrap text-sm font-medium transition-colors ${
                      selectedFilterState === filter
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            <div className='flex-1 overflow-auto'>
              <div className='divide-y divide-gray-200 dark:divide-gray-700/50'>
                {paginatedCases.map((caseItem) => (
                  <CaseListItem
                    key={caseItem.id}
                    caseItem={caseItem}
                    onDownload={onDownload}
                  />
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className='sticky bottom-0 border-t border-gray-200 bg-white p-4 dark:border-gray-700/50 dark:bg-gray-800'>
              <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
                <Button
                  variant='ghost'
                  onClick={() => handlePageChange(currentPageState - 1)}
                  disabled={currentPageState === 1}
                  className='flex w-full items-center gap-2.5 text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700/50 sm:w-auto'
                >
                  <ChevronLeft className='h-5 w-5' />
                  Previous
                </Button>
                <div className='flex items-center gap-4'>
                  {generatePaginationButtons(
                    currentPageState,
                    totalPages,
                    handlePageChange
                  )}
                </div>
                <Button
                  variant='ghost'
                  onClick={() => handlePageChange(currentPageState + 1)}
                  disabled={currentPageState === totalPages}
                  className='flex w-full items-center gap-2.5 text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700/50 sm:w-auto'
                >
                  Next
                  <ChevronRight className='h-5 w-5' />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function generatePaginationButtons(
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void
) {
  const buttons = [];

  // Always show first and last pages
  const showFirstPage = currentPage > 3;
  const showLastPage = currentPage < totalPages - 2;

  // Add first page if needed
  if (showFirstPage) {
    buttons.push(
      <div
        key='1'
        onClick={() => onPageChange(1)}
        className={`text-sm font-medium ${currentPage === 1 ? 'text-[#9176df]' : 'text-[#474754]'}`}
      >
        1
      </div>
    );
  }

  // Add first ellipsis if needed
  if (showFirstPage && currentPage > 3) {
    buttons.push(
      <div key='first-ellipsis' className='text-sm font-medium text-[#474754]'>
        ...
      </div>
    );
  }

  // Add surrounding pages
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  for (let page = startPage; page <= endPage; page++) {
    buttons.push(
      <div
        key={page}
        onClick={() => onPageChange(page)}
        className={`text-sm font-medium ${currentPage === page ? 'font-bold text-[#9176df]' : 'text-[#474754]'}`}
      >
        {page}
      </div>
    );
  }

  // Add last ellipsis if needed
  if (showLastPage && currentPage < totalPages - 2) {
    buttons.push(
      <div key='last-ellipsis' className='text-sm font-medium text-[#474754]'>
        ...
      </div>
    );
  }

  // Add last page if needed
  if (showLastPage) {
    buttons.push(
      <div
        key={totalPages}
        onClick={() => onPageChange(totalPages)}
        className={`text-sm font-medium ${currentPage === totalPages ? 'text-[#9176df]' : 'text-[#474754]'}`}
      >
        {totalPages}
      </div>
    );
  }

  return buttons;
}

function CaseOutcomeDetailSkeleton() {
  return (
    <div className='flex h-[80vh] w-full flex-col space-y-6 p-6'>
      <div className='grid gap-6 md:grid-cols-2' style={{ height: '50%' }}>
        <Card className='h-full p-6'>
          <Skeleton className='mb-6 h-6 w-1/2' />
          <div className='flex items-center justify-between'>
            <div>
              <Skeleton className='h-12 w-24' />
              <Skeleton className='mt-2 h-4 w-32' />
              <div className='mt-8 flex flex-wrap gap-4'>
                {[1, 2, 3].map((_, index) => (
                  <Skeleton key={index} className='h-8 w-24' />
                ))}
              </div>
            </div>
            <Skeleton className='h-24 w-24 rounded-full' />
          </div>
        </Card>
        <Card className='h-full p-6'>
          <Skeleton className='mb-6 h-6 w-1/2' />
          <Skeleton className='h-12 w-full' />
          <Skeleton className='mt-4 h-24 w-full' />
        </Card>
      </div>
      <Card style={{ height: '50%' }}>
        <div className='border-b border-[#e5e7eb] p-4'>
          <Skeleton className='h-10 w-full' />
        </div>
        <div className='divide-y divide-[#e5e7eb] overflow-auto'>
          {[1, 2, 3, 4, 5, 6, 7].map((_, index) => (
            <div key={index} className='flex items-center justify-between p-4'>
              <div className='flex items-center gap-4'>
                <Skeleton className='h-10 w-10 rounded-full' />
                <div>
                  <Skeleton className='mb-2 h-4 w-32' />
                  <Skeleton className='h-3 w-24' />
                </div>
              </div>
              <div className='flex items-center gap-4'>
                <Skeleton className='h-4 w-20' />
                <Skeleton className='h-8 w-20' />
                <Skeleton className='h-8 w-24' />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function CaseListItem({
  caseItem,
  onDownload,
}: {
  caseItem: Case;
  onDownload: (caseId: string) => void;
}) {
  const statusStyles = {
    Positive: {
      bgColor: 'bg-emerald-100 dark:bg-emerald-900',
      textColor: 'text-emerald-600 dark:text-emerald-400',
      dotColor: 'bg-emerald-500 dark:bg-emerald-400',
    },
    Neutral: {
      bgColor: 'bg-amber-100 dark:bg-amber-900',
      textColor: 'text-amber-600 dark:text-amber-400',
      dotColor: 'bg-amber-500 dark:bg-amber-400',
    },
    Negative: {
      bgColor: 'bg-rose-100 dark:bg-rose-900',
      textColor: 'text-rose-600 dark:text-rose-400',
      dotColor: 'bg-rose-500 dark:bg-rose-400',
    },
  };

  const { bgColor, textColor, dotColor } = statusStyles[caseItem.status];

  return (
    <div className='w-full border-b border-gray-200 transition-colors duration-200 last:border-b-0 hover:bg-gray-50 dark:border-gray-700/50 dark:hover:bg-gray-700'>
      <div className='grid grid-cols-1 items-start gap-4 p-4 sm:grid-cols-12 sm:items-center sm:px-6'>
        {/* Case Name */}
        <div className='col-span-1 flex items-center space-x-3 sm:col-span-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-6 w-6 text-gray-500 dark:text-gray-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          </div>
          <div>
            <h3 className='max-w-[200px] truncate text-sm font-semibold text-gray-900 dark:text-gray-100'>
              {caseItem.name}
            </h3>
            <p className='max-w-[200px] truncate text-xs text-gray-500 dark:text-gray-400'>
              Case Reference
            </p>
          </div>
        </div>

        {/* Location */}
        <div className='col-span-1 flex flex-col sm:col-span-3'>
          <span className='truncate text-sm font-medium text-gray-900 dark:text-gray-100'>
            {caseItem.location}
          </span>
          <span className='text-xs text-gray-500 dark:text-gray-400'>
            Jurisdiction
          </span>
        </div>

        {/* Date */}
        <div className='col-span-1 flex flex-col sm:col-span-2'>
          <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
            {caseItem.date}
          </span>
          <span className='text-xs text-gray-500 dark:text-gray-400'>
            Filed Date
          </span>
        </div>

        {/* Status */}
        <div className='col-span-1 sm:col-span-2'>
          <div
            className={`inline-flex items-center space-x-2 px-3 py-1 ${bgColor} rounded-full`}
          >
            <span className={`h-2 w-2 ${dotColor} rounded-full`}></span>
            <span className={`text-xs font-medium ${textColor}`}>
              {caseItem.status}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className='col-span-1 flex justify-start sm:col-span-2 sm:justify-end'>
          <button
            onClick={() => onDownload(caseItem.id)}
            className='
              flex items-center space-x-2
              rounded-lg border
              border-gray-200 px-3
              py-2 text-sm
              text-gray-700 transition-colors duration-200
              hover:bg-gray-100
              focus:outline-none
              focus:ring-2 focus:ring-purple-500/50
              dark:border-gray-700
              dark:text-gray-300
              dark:hover:bg-gray-700/50
            '
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-4 w-4 text-gray-500 dark:text-gray-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
              />
            </svg>
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({
  count,
  label,
  color,
  bgColor,
}: {
  count: number;
  label: string;
  color: string;
  bgColor: string;
}) {
  return (
    <div className='flex items-center gap-2'>
      <div
        className={`flex items-center gap-2 rounded-full px-3 py-1.5 ${bgColor}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${color} opacity-80`} />
        <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
          {count}
        </span>
        <span className={`text-xs ${color}`}>{label}</span>
      </div>
    </div>
  );
}

function CircularProgress({
  value,
  total,
  status,
  label,
}: {
  value: number;
  total: number;
  status: CaseStatus;
  label?: string;
}) {
  const percentage = (value / total) * 100;
  const radius = 75;
  const circumference = 2 * Math.PI * radius;
  const strokeWidth = 4;
  const size = 190;

  const getStatusColor = (status: CaseStatus) => {
    switch (status) {
      case 'Positive':
        return 'text-emerald-500 dark:text-emerald-400';
      case 'Neutral':
        return 'text-amber-500 dark:text-amber-400';
      case 'Negative':
        return 'text-rose-500 dark:text-rose-400';
    }
  };

  return (
    <div
      className='relative hidden sm:block'
      style={{ width: size, height: size }}
    >
      <svg className='-rotate-90 transform' width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke='currentColor'
          strokeOpacity={0.1}
          strokeWidth={strokeWidth}
          className='text-gray-200 dark:text-gray-700'
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke='currentColor'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (percentage / 100) * circumference}
          className={`${getStatusColor(status)} opacity-80`}
        />
      </svg>
      <div className='absolute inset-0 flex flex-col items-center justify-center'>
        <span className='text-4xl font-bold text-gray-900 dark:text-gray-100'>
          {value}
        </span>
        {label && (
          <span className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            {label}
          </span>
        )}
      </div>
      {status === 'Negative' && (
        <div className='absolute -bottom-1 left-1/2 -translate-x-1/2 transform'>
          <span className='inline-flex items-center rounded-sm bg-gray-900 px-2 py-0.5 text-xs font-medium text-white dark:bg-gray-100 dark:text-gray-900'>
            Poor
          </span>
        </div>
      )}
    </div>
  );
}
